<script lang="ts">
  import { onMount } from 'svelte'
  import { tStore } from '../../i18n'
  import { analyticsApi, type CriteriaAnalytics } from '../../api/analytics'
  import PageHeader from '../../components/PageHeader.svelte'
  import SatisfactionDonut from '../../components/charts/SatisfactionDonut.svelte'
  import CategoryBar, { type CategoryRow } from '../../components/charts/CategoryBar.svelte'
  import CriteriaStatusGroup from '../../components/CriteriaStatusGroup.svelte'
  import { useAbortable } from '../../composables/useAbortable'
  import {
    orderedSections,
    overallTotals,
    percent as pct,
    splitByStatus,
  } from './certificateAnalysis'

  let data = $state<CriteriaAnalytics | null>(null)
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)

  const { start } = useAbortable()

  onMount(() => {
    void load(start())
  })

  async function load(signal: AbortSignal) {
    loading = true
    errorMsg = null
    try {
      data = await analyticsApi.criteria({ signal })
    } catch (err) {
      if (signal.aborted) return
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  let sections = $derived(data ? orderedSections(data) : [])
  let overall = $derived(data ? overallTotals(data) : { satisfied: 0, total: 0 })
  let overallPercent = $derived(pct(overall.satisfied, overall.total))

  function categoryLabel(key: string): string {
    const label = $tStore(`views.procurementDetail.aiScore.category.${key}`)
    // Unknown/extra categories have no i18n key — resolve() returns the raw path,
    // so fall back to the bare key.
    return label.startsWith('views.') ? key : label
  }

  // Rows for the comparative overview bar — one per category with data.
  let categoryRows = $derived<CategoryRow[]>(
    sections
      .filter((s) => s.stats.total > 0)
      .map((s) => ({
        label: categoryLabel(s.key),
        satisfied: s.stats.satisfied,
        total: s.stats.total,
      })),
  )
</script>

<section class="analysis">
  <PageHeader
    title={$tStore('views.certificateAnalysis.title')}
    subtitle={$tStore('views.certificateAnalysis.subtitle')}
  />

  {#if loading && !data}
    <div
      class="skeletons"
      aria-label={$tStore('views.certificateAnalysis.loading')}
      aria-busy="true"
    >
      <div class="skeleton skeleton--kpis"></div>
      <div class="skeleton skeleton--chart"></div>
      <div class="skeleton skeleton--chart"></div>
    </div>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if data && data.analysedTenders === 0}
    <article class="card empty">
      <p class="muted">{$tStore('views.certificateAnalysis.empty')}</p>
    </article>
  {:else if data}
    <!-- Summary KPI row -->
    <div class="kpis">
      <article class="card kpi">
        <span class="kpi__label">{$tStore('views.certificateAnalysis.kpi.analysedTenders')}</span>
        <span class="kpi__value">{data.analysedTenders}</span>
      </article>
      <article class="card kpi">
        <span class="kpi__label">{$tStore('views.certificateAnalysis.kpi.criteriaEvaluated')}</span>
        <span class="kpi__value">{overall.total}</span>
      </article>
      <article class="card kpi">
        <span class="kpi__label">{$tStore('views.certificateAnalysis.kpi.criteriaSatisfied')}</span>
        <span class="kpi__value">{overall.satisfied}</span>
      </article>
      <article class="card kpi">
        <span class="kpi__label">{$tStore('views.certificateAnalysis.kpi.overallSatisfied')}</span>
        <span class="kpi__value">{overallPercent}%</span>
      </article>
    </div>

    <!-- Comparative overview: satisfied vs. not, across the categories -->
    {#if categoryRows.length > 0}
      <article class="card overview">
        <header class="overview__head">
          <h3>{$tStore('views.certificateAnalysis.overviewTitle')}</h3>
          <ul class="legend legend--inline">
            <li>
              <span class="legend__swatch legend__swatch--sat"></span>
              {$tStore('views.certificateAnalysis.legend.satisfied')}
            </li>
            <li>
              <span class="legend__swatch legend__swatch--unsat"></span>
              {$tStore('views.certificateAnalysis.legend.notSatisfied')}
            </li>
          </ul>
        </header>
        <CategoryBar rows={categoryRows} />
      </article>
    {/if}

    <!-- Per-category analysis -->
    {#each sections as section (section.key)}
      {@const split = splitByStatus(section.stats)}
      <article class="card category">
        <header class="category__head">
          <h3>{categoryLabel(section.key)}</h3>
          <span class="category__meta">
            {pct(section.stats.satisfied, section.stats.total)}%
            {$tStore('views.certificateAnalysis.satisfiedSuffix')}
            · {section.stats.satisfied}/{section.stats.total}
          </span>
        </header>

        {#if section.stats.total === 0}
          <p class="muted">{$tStore('views.certificateAnalysis.noCategoryData')}</p>
        {:else}
          <div class="category__grid">
            <!-- Donut: satisfied vs. not -->
            <div class="donut-block">
              <SatisfactionDonut
                satisfied={section.stats.satisfied}
                total={section.stats.total}
                label={categoryLabel(section.key)}
              />
              <ul class="legend">
                <li>
                  <span class="legend__swatch legend__swatch--sat"></span>
                  {$tStore('views.certificateAnalysis.legend.satisfied')}
                  <strong>{section.stats.satisfied}</strong>
                </li>
                <li>
                  <span class="legend__swatch legend__swatch--unsat"></span>
                  {$tStore('views.certificateAnalysis.legend.notSatisfied')}
                  <strong>{section.stats.total - section.stats.satisfied}</strong>
                </li>
              </ul>
            </div>

            <!--
              Requirements split by whether OG-CS meets them. Satisfaction is
              binary per criterion (met every time or never), so a ratio bar
              carried no information — instead each list is ordered by how often
              the criterion was required, and that frequency is the badge. Each
              list collapses to its top few, with a "view more" toggle.
            -->
            <div class="status-groups">
              <CriteriaStatusGroup
                title={$tStore('views.certificateAnalysis.metTitle')}
                variant="met"
                items={split.met}
              />
              <CriteriaStatusGroup
                title={$tStore('views.certificateAnalysis.unmetTitle')}
                variant="unmet"
                items={split.unmet}
              />
            </div>
          </div>
        {/if}
      </article>
    {/each}
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .analysis {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
    // Satisfied / unmet — kept in sync with the Chart.js palette in
    // `chartTheme.ts` (which can't read SCSS tokens) so charts, legend, and the
    // per-criterion bars all speak the same green/red.
    --ok: #16a34a;
    --bad: #dc2626;
  }

  .empty {
    text-align: center;
    padding: v.$space-2xl;
  }

  // Loading placeholders — shimmer blocks standing in for the KPI row and the
  // two chart cards while the first fetch is in flight.
  .skeletons {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .skeleton {
    border-radius: v.$radius-lg;
    background: linear-gradient(
      90deg,
      v.$color-surface 0%,
      v.$color-border 50%,
      v.$color-surface 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.4s ease infinite;
  }

  .skeleton--kpis {
    height: 96px;
  }

  .skeleton--chart {
    height: 220px;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .overview {
    display: flex;
    flex-direction: column;
    gap: v.$space-md;
  }

  .overview__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-md;
    flex-wrap: wrap;

    h3 {
      margin: 0;
    }
  }

  .legend--inline {
    flex-direction: row;
    gap: v.$space-md;
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: v.$space-md;

    @media (max-width: v.$bp-lg) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: v.$bp-sm) {
      grid-template-columns: 1fr;
    }
  }

  .kpi {
    display: flex;
    flex-direction: column;
    gap: v.$space-sm;
    position: relative;
    overflow: hidden;
    padding: v.$space-lg;

    &::before {
      content: '';
      position: absolute;
      inset: 0 auto 0 0;
      width: 3px;
      background: linear-gradient(180deg, v.$color-primary, #6366f1);
      opacity: 0.85;
    }

    &__label {
      color: v.$color-text-muted;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    &__value {
      font-size: 2rem;
      font-weight: 700;
      line-height: 1;
      letter-spacing: -0.02em;
    }
  }

  .category {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .category__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: v.$space-md;
    flex-wrap: wrap;

    h3 {
      margin: 0;
    }
  }

  .category__meta {
    color: v.$color-text-muted;
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
  }

  .category__grid {
    display: grid;
    grid-template-columns: minmax(200px, 260px) 1fr;
    gap: v.$space-xl;
    align-items: center;

    @media (max-width: v.$bp-md) {
      grid-template-columns: 1fr;
      gap: v.$space-lg;
    }
  }

  .donut-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: v.$space-md;
  }

  .legend {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: v.$space-xs;
    font-size: 0.875rem;
    color: v.$color-text-muted;

    li {
      display: flex;
      align-items: center;
      gap: v.$space-sm;
    }

    strong {
      margin-left: auto;
      color: v.$color-text;
      font-variant-numeric: tabular-nums;
    }
  }

  .legend__swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;

    // Match the Chart.js donut/bar slice colours exactly.
    &--sat {
      background: var(--ok);
    }

    &--unsat {
      background: var(--bad);
    }
  }

  // Two side-by-side lists: requirements OG-CS meets vs. doesn't. Collapses to
  // a single column on narrow screens.
  // Two side-by-side lists (met vs. not), each its own CriteriaStatusGroup.
  // Collapses to a single column on narrow screens.
  .status-groups {
    min-width: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: v.$space-lg;

    @media (max-width: v.$bp-sm) {
      grid-template-columns: 1fr;
    }
  }
</style>
